import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Container } from 'components/GridV2';

import { IQueryParams, ISanityCompareTiers } from 'typescriptDefs';

import { trackEvent, useIsMobileSize } from 'helpers';

import TabsList from '../tabs/TabsList';
import TabsTrigger from '../tabs/TabsTrigger';
import TiersTableContent from './TiersTableContent';

interface TiersCompareTableProps {
  category?: string;
  className?: string;
  data: ISanityCompareTiers;
  queryParams?: IQueryParams;
  toggleBenefits?: boolean;
}

export type TTiersPaymentPeriod = 'monthly' | 'yearly';

const TiersCompareTable = ({ data, className, toggleBenefits, queryParams, category }: TiersCompareTableProps) => {
  const isMobile = useIsMobileSize();

  const [tiersPaymentPeriod, setTiersPaymentPeriod] = useState<TTiersPaymentPeriod>('yearly');
  const [selectedTier, setSelectedTier] = useState<'essential' | 'extra' | 'everything'>('essential');
  const activeIndex = data.tiers.findIndex((tier) => tier.variant === selectedTier);

  useEffect(() => {
    trackEvent('Clicked', { name: `switch-tier-payment-period-${tiersPaymentPeriod}` });
  }, [tiersPaymentPeriod]);

  return (
    <Container className={twMerge('px-0 xs:px-32', className)}>
      {isMobile ? (
        // Mobile view - Show tier filter tabs
        <div className="px-18">
          <TabsList className="mb-16 flex w-full items-center justify-between" activeIndex={activeIndex}>
            {data.tiers.map((tier) => (
              <TabsTrigger
                key={tier.variant}
                isActiveTab={tier.variant === selectedTier}
                onClick={() => {
                  setSelectedTier(tier.variant);
                }}
              >
                {tier.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {data.tiers
            .filter((val) => val.variant === selectedTier)
            .map((tier) => (
              <div key={tier.variant}>
                <TiersTableContent
                  category={category}
                  footerNotes={data.footerNotes}
                  selectedTiers={[tier]}
                  tabLabelYearly={data.tabLabelYearly}
                  tableHeader={data.tableHeader}
                  tiers={data.tiers}
                  toggleBenefits={toggleBenefits}
                  queryParams={queryParams}
                  paymentPeriod={tiersPaymentPeriod}
                  setPaymentPeriod={setTiersPaymentPeriod}
                />
              </div>
            ))}
        </div>
      ) : (
        // Desktop view - Show only monthly/yearly tabs and all tiers
        <>
          <TabsList className="max-w-[206px]" activeIndex={tiersPaymentPeriod === 'monthly' ? 0 : 1}>
            <TabsTrigger
              onClick={() => {
                setTiersPaymentPeriod('monthly');
              }}
              isActiveTab={tiersPaymentPeriod === 'monthly'}
            >
              {data.tabLabelMonthly}
            </TabsTrigger>
            <TabsTrigger
              isActiveTab={tiersPaymentPeriod === 'yearly'}
              onClick={() => {
                setTiersPaymentPeriod('yearly');
              }}
            >
              {data.tabLabelYearly}
            </TabsTrigger>
          </TabsList>
          <TiersTableContent
            category={category}
            footerNotes={data.footerNotes}
            selectedTiers={data.tiers}
            tabLabelYearly={data.tabLabelYearly}
            tableHeader={data.tableHeader}
            tiers={data.tiers}
            toggleBenefits={toggleBenefits}
            queryParams={queryParams}
            paymentPeriod={tiersPaymentPeriod}
            setPaymentPeriod={setTiersPaymentPeriod}
          />
        </>
      )}
    </Container>
  );
};

export default TiersCompareTable;
